/**
 * src/pages/WhereIsEverythingGoing/sections/HeroVisual.tsx
 * Signature visual for the page hero: a star-lifecycle arc / fading-galaxies SVG
 * depicting stars igniting, swelling to a red giant, and dimming into the dark horizon.
 * Fully responsive, keyboard accessible, reduced-motion static.
 */

import React from "react";
import { useReducedMotion } from "@/components/cosmic/useReducedMotion";
export function HeroVisual() {
  const prefersReduced = useReducedMotion();
  return <div className="relative w-full max-w-sm aspect-square rounded-2xl p-4 flex flex-col items-center justify-center ql-glass border border-primary/30 shadow-[0_0_24px_var(--glow-primary)] select-none overflow-hidden" role="img" aria-label="Star lifecycle trajectory diagram: young main sequence star, expanding red giant, planetary nebula, and final cooling dwarf on deep cosmic background.">
      {/* Background radial glow */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,var(--primary)/15_0%,transparent_70%)] pointer-events-none" aria-hidden="true" />

      <svg viewBox="0 0 320 320" className="w-full h-full relative z-10" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          {/* Gradients for star phases */}
          <radialGradient id="youngStarGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="hsl(186 100% 90%)" />
            <stop offset="60%" stopColor="hsl(186 100% 50%)" />
            <stop offset="100%" stopColor="hsl(186 100% 50% / 0)" />
          </radialGradient>

          <radialGradient id="redGiantGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="hsl(38 95% 75%)" />
            <stop offset="45%" stopColor="hsl(0 84% 60%)" />
            <stop offset="85%" stopColor="hsl(270 70% 55% / 0.4)" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>

          <radialGradient id="nebulaShell" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="hsl(186 100% 50% / 0.8)" />
            <stop offset="50%" stopColor="hsl(270 70% 55% / 0.4)" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>

          <radialGradient id="whiteDwarf" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="hsl(0 0% 100%)" />
            <stop offset="40%" stopColor="hsl(186 100% 70%)" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>

          <linearGradient id="orbitArc" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="hsl(186 100% 50%)" stopOpacity="0.8" />
            <stop offset="50%" stopColor="hsl(0 84% 60%)" stopOpacity="0.8" />
            <stop offset="100%" stopColor="hsl(240 20% 50%)" stopOpacity="0.2" />
          </linearGradient>
        </defs>

        {/* Outer orbital calibration rings */}
        <circle cx="160" cy="160" r="140" stroke="hsl(186 100% 50% / 0.15)" strokeWidth="1" strokeDasharray="4 6" className={prefersReduced ? "" : "animate-orbital-rotation"} style={{
        transformOrigin: "160px 160px"
      }} />
        <circle cx="160" cy="160" r="115" stroke="hsl(215 100% 60% / 0.15)" strokeWidth="1" />

        {/* Trajectory Arc */}
        <path d="M 40 220 C 60 80, 200 40, 270 140 C 290 170, 280 240, 220 260" stroke="url(#orbitArc)" strokeWidth="2" strokeDasharray="6 4" />

        {/* Phase 1: Ignition / Young Main Sequence */}
        <g transform="translate(50, 190)">
          <circle cx="0" cy="0" r="18" fill="url(#youngStarGlow)" />
          <circle cx="0" cy="0" r="6" fill="hsl(0 0% 98%)" />
          <text x="0" y="30" textAnchor="middle" fill="hsl(186 100% 50%)" fontSize="9" fontFamily="var(--font-mono)" letterSpacing="0.08em">
            0 Gyr • IGNITION
          </text>
        </g>

        {/* Phase 2: Red Giant Expansion */}
        <g transform="translate(180, 75)">
          <circle cx="0" cy="0" r="44" fill="url(#redGiantGlow)" className={prefersReduced ? "" : "animate-pulse"} />
          <circle cx="0" cy="0" r="16" fill="hsl(0 84% 60% / 0.8)" />
          <circle cx="0" cy="0" r="4" fill="hsl(0 0% 98%)" />
          <text x="0" y="-48" textAnchor="middle" fill="hsl(0 84% 70%)" fontSize="9" fontFamily="var(--font-mono)" letterSpacing="0.08em">
            ~11.5 Gyr • RED GIANT
          </text>
        </g>

        {/* Phase 3: Planetary Nebula Shedding */}
        <g transform="translate(265, 160)">
          <circle cx="0" cy="0" r="26" fill="url(#nebulaShell)" stroke="hsl(186 100% 50% / 0.4)" strokeWidth="1" strokeDasharray="2 3" />
          <circle cx="0" cy="0" r="5" fill="hsl(186 100% 80%)" />
          <text x="0" y="38" textAnchor="middle" fill="hsl(215 100% 75%)" fontSize="8" fontFamily="var(--font-mono)">
            NEBULA
          </text>
        </g>

        {/* Phase 4: White Dwarf & Event Horizon Dimming */}
        <g transform="translate(210, 255)">
          <circle cx="0" cy="0" r="10" fill="url(#whiteDwarf)" />
          <circle cx="0" cy="0" r="3" fill="hsl(0 0% 100%)" />
          <text x="0" y="22" textAnchor="middle" fill="hsl(240 8% 68%)" fontSize="9" fontFamily="var(--font-mono)" letterSpacing="0.08em">
            &gt; 12 Gyr • WHITE DWARF
          </text>
        </g>

        {/* Distant Redshifted Galaxy Nodes */}
        <g opacity="0.45">
          <circle cx="110" cy="140" r="1.5" fill="hsl(0 84% 60%)" />
          <circle cx="120" cy="240" r="1.5" fill="hsl(0 84% 60%)" />
          <circle cx="140" cy="280" r="1" fill="hsl(0 84% 60%)" />
        </g>
      </svg>

      {/* Caption badge */}
      <div className="mt-2 text-center">
        <span className="font-mono text-[10px] tracking-widest text-primary uppercase bg-primary/10 px-2 py-0.5 rounded border border-primary/30">
          Stellar Arc & Cosmic Fate
        </span>
      </div>
    </div>;
}